import Controller, { OnClick, OnDelete, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames } from '../../../../codegen';
import { WoundsImagesListAliases } from '../../../../codegen/constants/aliases/wounds-images-list-aliases';
import BaseController from '../../../../controllers/BaseController';
import { DeleteEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FetchAPI, GalleryAPI, GalleryProperties, GalleryProps, PaginatorAPI, PanelAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import DialogService from '../../../../services/dialog-service';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ApplicationRoute } from '../../../../constants/routes';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { WoundsDressingContext } from '../../wounds-dressing-service';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';

const GALLERY_INITIAL_STATE = {
  type: GalleryProperties.type.defaultValue,
};

type WoundsImagesType = {
  fileLocation: string,
  id: number,
  photoDate: string,
  releaseDate: string,
  username: string,
  woundId: number,
}

@Controller({ fragment: FragmentNames.WOUNDS_IMAGES_LIST })
class WoundsImagesListController extends BaseController {

  @Inject(WoundsImagesListAliases.GALLERY_WOUNDS_IMAGE_LIST)
  declare galleryAPI: GalleryAPI;

  @Inject(WoundsImagesListAliases.FETCH_WOUNDS_IMAGE_LIST)
  private fetchImagesApi!: FetchAPI;

  @Inject(WoundsImagesListAliases.PANEL_FETCH_WOUNDS_IMAGE_LIST)
  private panelFetchWoundsListApi!: PanelAPI;

  @Inject(WoundsImagesListAliases.PANEL_LOADER)
  private panelLoaderApi!: PanelAPI;

  @Inject(WoundsImagesListAliases.FETCH_WOUNDS_IMAGE_LIST)
  private fetchWoundsImageApi!: FetchAPI;

  @Inject(WoundsImagesListAliases.PANEL_BUTTON)
  private panelGlobalActionsApi!: PanelAPI;

  updateGallery(gallery: GalleryProps): void {
    this.galleryAPI.setSelectable(Boolean(gallery.selectable));
    this.galleryAPI.setType(gallery.type ?? GalleryProperties.type.defaultValue);
  }

  @OnMount({target: WoundsImagesListAliases.LAYOUT_WOUNDS_IMAGE_LIST })
  handleMountLayout(): void {
    const { woundId } = this.historyService.getCurrentRouteParams();
    this.context.set({ woundId});

    const allowInfo = InformationBarUtils.allowInfo && WoundsDressingContext.getAllowEdit();
    this.panelGlobalActionsApi.setVisible(allowInfo);
  }

  @OnMount(WoundsImagesListAliases.PAGINATOR_WOUNDS_IMAGE_LIST)
  onPaginatorMount(event: MountEvent): void {
    const api = event.target as PaginatorAPI;
    api.setPage(0);
  }

  @OnMount(WoundsImagesListAliases.GALLERY_WOUNDS_IMAGE_LIST)
  onGalleryMount(): void {
    this.updateGallery(GALLERY_INITIAL_STATE);
  }

  @OnDelete(WoundsImagesListAliases.GALLERY_WOUNDS_IMAGE_LIST)
  handleDeleteImage(event: DeleteEvent): void {
    const woundImageId = Number(event.id);
    const woundsImages = this.fetchWoundsImageApi.getData() as WoundsImagesType[]
    const allowInfo = InformationBarUtils.allowInfo && WoundsDressingContext.getAllowEdit();
    const isReleased = woundsImages?.some(woundImage =>
      Number(woundImage.id) === woundImageId ? !!woundImage.releaseDate : false
    )

    if (isReleased || !allowInfo) {
      const text = !allowInfo ? 'txt.no-permission-for-deleting-the-record' : 'text.cannot-delete-records-already-released';
      this.showToast(MessageService.translate(text), ToastLevel.ERROR);
      return
    }
    this.confirmDelete(Number(woundImageId));
  }

  private confirmDelete(woundImageId: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { applicationContext.popNavigationItem() },
      onClickOk: () => { this.delete(woundImageId)},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private delete(woundImageId: number): void {
    this.panelFetchWoundsListApi.setVisible(false)
    this.panelLoaderApi.setVisible(true);
    HttpService.delete(Endpoint.DELETE_WOUNDS_IMAGE, { woundImageId })
      .then(() => {
        this.showToast(MessageService.translate('text.removed-with-success'), ToastLevel.SUCCESS)
        this.fetchImagesApi.forceFetch();
      }).catch((err) => {
        if (err?.body?.errorMessage) {
          this.showToast(MessageService.translate('tws.generic-backend-error.call-administrator'), ToastLevel.ERROR);
          return
        }
      }).finally(() => {
        this.panelFetchWoundsListApi.setVisible(true)
        this.panelLoaderApi.setVisible(false);
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'wounds-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  @OnClick(WoundsImagesListAliases.BUTTON_ADD_WOUNDS_IMAGE_LIST)
  handleClickAddButton(): void {
    const { encounterId, patientId, woundId } = this.historyService.getCurrentRouteParams();
    const route = ApplicationRoute.WOUND_PICTURE_EDIT;

    applicationContext.redirect(route, {
      encounterId,
      patientId,
      woundId
    });
  }
}

export default WoundsImagesListController;
